import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as REPointerEvent,
} from 'react';
import type { ImageFilter, ImageMask } from '@ox/schema';
import { maskImageUrl } from '../lib/masks';
import { bakeImageFilter } from '../lib/imageFilters';
import { maskPhotoLayout } from '../lib/maskFit';

export type MaskFitState = {
  maskScale: number;
  maskOffsetX: number;
  maskOffsetY: number;
};

type Props = {
  src: string;
  mask: ImageMask;
  filter?: ImageFilter;
  fit: MaskFitState;
  onFitChange: (fit: MaskFitState) => void;
  className?: string;
};

/**
 * Photo under a fixed mask: drag to pan, slider to zoom.
 * Without a mask, shows a letterboxed photo in a fixed square stage.
 *
 * Free-mode (sin marco) uses an in-flow <img> — absolute-only children + % widths
 * inside CSS grid `place-items:center` collapsed to 0×0 on mobile WebKit, which
 * looked like a blank preview until applying a mask (hint text gave width).
 */
export function MaskFitPreview({ src, mask, filter = 'none', fit, onFitChange, className }: Props) {
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [displaySrc, setDisplaySrc] = useState(src);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [imgFailed, setImgFailed] = useState(false);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origOx: number;
    origOy: number;
  } | null>(null);

  const maskUrl = maskImageUrl(mask);
  const interactive = mask !== 'none' && !!maskUrl;
  const { maskScale: scale, maskOffsetX: ox, maskOffsetY: oy } = fit;

  const syncNaturalFromImg = useCallback(() => {
    const el = imgRef.current;
    if (!el || el.naturalWidth <= 0 || el.naturalHeight <= 0) return;
    const w = el.naturalWidth;
    const h = el.naturalHeight;
    setNatural((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    setImgFailed(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setNatural(null);
    setImgFailed(false);
    if (!src) {
      setDisplaySrc('');
      return;
    }
    if (!filter || filter === 'none') {
      setDisplaySrc(src);
      return;
    }
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => {
      if (cancelled) return;
      try {
        const baked = bakeImageFilter(i, filter);
        if (baked instanceof HTMLCanvasElement) {
          setDisplaySrc(baked.toDataURL('image/png'));
          return;
        }
      } catch {
        /* fall through */
      }
      setDisplaySrc(src);
    };
    i.onerror = () => {
      if (!cancelled) setDisplaySrc(src);
    };
    i.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, filter]);

  useLayoutEffect(() => {
    syncNaturalFromImg();
  }, [displaySrc, syncNaturalFromImg]);

  const layout = useMemo(() => {
    if (!natural) return { x: 0, y: 0, width: 100, height: 100, maxPanX: 0, maxPanY: 0 };
    const box = 100;
    const placed = maskPhotoLayout(box, box, natural.w, natural.h, scale, ox, oy);
    const cover = Math.max(box / natural.w, box / natural.h);
    const width = natural.w * cover * Math.max(1, scale);
    const height = natural.h * cover * Math.max(1, scale);
    return {
      ...placed,
      maxPanX: Math.max(0, (width - box) / 2),
      maxPanY: Math.max(0, (height - box) / 2),
    };
  }, [natural, scale, ox, oy]);

  const canPan = interactive && (layout.maxPanX > 0.05 || layout.maxPanY > 0.05);

  const onPointerDown = useCallback(
    (e: REPointerEvent<HTMLDivElement>) => {
      if (!canPan) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      drag.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        origOx: ox,
        origOy: oy,
      };
    },
    [canPan, ox, oy],
  );

  const onPointerMove = useCallback(
    (e: REPointerEvent<HTMLDivElement>) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId || !stageRef.current) return;
      const rect = stageRef.current.getBoundingClientRect();
      const maxPanXpx = (layout.maxPanX / 100) * rect.width;
      const maxPanYpx = (layout.maxPanY / 100) * rect.height;
      if (maxPanXpx <= 0 && maxPanYpx <= 0) return;
      onFitChange({
        maskScale: scale,
        maskOffsetX:
          maxPanXpx > 0 ? clamp(d.origOx + (e.clientX - d.startX) / maxPanXpx, -1, 1) : 0,
        maskOffsetY:
          maxPanYpx > 0 ? clamp(d.origOy + (e.clientY - d.startY) / maxPanYpx, -1, 1) : 0,
      });
    },
    [onFitChange, scale, layout.maxPanX, layout.maxPanY],
  );

  const endDrag = useCallback((e: REPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId === e.pointerId) drag.current = null;
  }, []);

  const stageStyle: CSSProperties = {
    ...(maskUrl
      ? {
          WebkitMaskImage: `url(${maskUrl})`,
          maskImage: `url(${maskUrl})`,
          WebkitMaskSize: '100% 100%',
          maskSize: '100% 100%',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
        }
      : {}),
  };

  const maskedImgStyle: CSSProperties = {
    position: 'absolute',
    left: `${layout.x}%`,
    top: `${layout.y}%`,
    width: `${layout.width}%`,
    height: `${layout.height}%`,
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'fill',
    cursor: canPan ? 'grab' : 'default',
    touchAction: 'none',
    userSelect: 'none',
  };

  return (
    <div className={`ox-mask-fit-root${className ? ` ${className}` : ''}`}>
      <div
        ref={stageRef}
        className={`ox-mask-fit-stage${interactive ? ' ox-mask-fit-stage--active' : ' ox-mask-fit-stage--free'}`}
        style={stageStyle}
        onPointerDown={interactive ? onPointerDown : undefined}
        onPointerMove={interactive ? onPointerMove : undefined}
        onPointerUp={interactive ? endDrag : undefined}
        onPointerCancel={interactive ? endDrag : undefined}
      >
        {interactive ? <span className="ox-mask-fit-strut" aria-hidden /> : null}
        {displaySrc ? (
          <img
            ref={imgRef}
            key={displaySrc}
            src={displaySrc}
            alt=""
            className={interactive ? undefined : 'ox-mask-fit-free-img'}
            style={interactive ? maskedImgStyle : undefined}
            draggable={false}
            onLoad={syncNaturalFromImg}
            onError={() => setImgFailed(true)}
          />
        ) : null}
        {imgFailed && (
          <div className="ox-mask-fit-error">No se pudo mostrar la imagen</div>
        )}
      </div>
      {interactive && (
        <div className="ox-mask-fit-zoom">
          <span aria-hidden>−</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={scale}
            aria-label="Ampliar imagen"
            onChange={(e) => {
              const maskScale = Number(e.target.value) || 1;
              onFitChange({
                maskScale,
                maskOffsetX: ox,
                maskOffsetY: oy,
              });
            }}
          />
          <span aria-hidden>+</span>
        </div>
      )}
      {interactive && (
        <p className="ox-hint ox-mask-fit-hint">
          {canPan
            ? 'Arrastra la imagen para elegir la zona visible. Usa el zoom si quieres acercar.'
            : 'Usa el zoom para ampliar y luego arrastra para encuadrar.'}
        </p>
      )}
    </div>
  );
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

/** Resolve when an image URL is decodable (or fail). Used before swapping blob→CDN. */
export function preloadImageUrl(url: string, timeoutMs = 12000): Promise<boolean> {
  if (!url) return Promise.resolve(false);
  if (url.startsWith('blob:') || url.startsWith('data:')) return Promise.resolve(true);
  return new Promise((resolve) => {
    const img = new Image();
    const done = (ok: boolean) => {
      window.clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve(ok);
    };
    const timer = window.setTimeout(() => done(false), timeoutMs);
    img.onload = () => done(true);
    img.onerror = () => done(false);
    img.src = url;
  });
}
